/**
 * Deals Monitoring: the named strategic accounts under active follow-up.
 *
 * Distinct from the leads pipeline on purpose — leads are the funnel that runs
 * Cold to Won, deals are the handful of accounts someone is personally
 * chasing. Sections are the primary structure and are ordinary editable rows;
 * stage is a label, not the spine.
 */

export type Pipeline = 'cg_deals' | 'partnerships';

export const PIPELINE_LABELS: Record<Pipeline, string> = {
  cg_deals: 'CG Deals',
  partnerships: 'Partnerships',
};

export interface DealSection {
  id: string;
  pipeline: Pipeline;
  name: string;
  position: number;
  /**
   * This section collects starred deals for its pipeline. At most one per
   * pipeline, enforced by a partial unique index. Found by this flag rather
   * than by the name so renaming it does not stop the move. See
   * 20270130000100 and starMove.ts.
   */
  holds_starred: boolean;
}

export interface DealStage {
  id: string;
  pipeline: Pipeline;
  name: string;
  position: number;
}

export interface Deal {
  id: string;
  pipeline: Pipeline;
  name: string;
  company_id: string | null;
  /** The Outbound lead this deal is aimed at, if any. */
  lead_id: string | null;
  stage_id: string | null;
  section_id: string | null;
  owner_id: string | null;
  next_action_date: string | null;
  deadline?: string | null;
  brief_color?: 'red' | 'yellow' | 'green' | null;
  /**
   * What this deal is worth, in USD. NULL means NOBODY HAS PRICED IT, which is
   * deliberately not zero — the board's column totals add the priced ones and
   * say how many they could not add, rather than counting a blank as nothing.
   * See dealAmount.ts and 20270104000100.
   */
  amount: number | null;
  /**
   * Surface this deal in the daily CEO brief appendix.
   *
   * Orthogonal to section, stage and pipeline on purpose. A deal can only be
   * in one section, so making the brief a section would have cost the triage
   * grouping to buy the appendix. See 20270127000100.
   *
   * This is the half a human controls. The brief also prints deals that have
   * gone silent, which is computed, because forgetting is the failure this
   * exists to answer and a flag cannot fix forgetting on its own.
   */
  in_ceo_brief: boolean;
  /**
   * Where this deal sat when it was starred, so unstarring can put it back.
   * NULL when not starred, or when it had no section at the time. Never read
   * to decide whether a deal IS starred: in_ceo_brief is the only answer to
   * that. See starMove.ts.
   */
  section_before_star: string | null;
  /**
   * What happened commercially. Separate from `stage_id` on purpose: a lost
   * deal keeps the stage it reached, so "how far did the deals we lost get"
   * stays answerable. See 20261125000100.
   */
  outcome: 'open' | 'won' | 'lost';
  /** The day it was won or lost. Null while open, and null on the legacy deals
   *  created for pre-restructure customers — nobody recorded when those closed. */
  closed_on: string | null;
  outcome_note: string;
  /**
   * Where this deal lives in HubSpot, which is where the official financial
   * record lives. A link and nothing else — no HubSpot field is copied into
   * the Hub, so the two cannot disagree about anything but this address.
   * Null means nobody has linked it. See hubspotLink.ts and 20270120000100.
   */
  hubspot_url: string | null;
  /**
   * Hanneza's checklist, the admin half: has the HubSpot record been brought
   * up to date (contacts, company, stage, amount)? A boolean on the deal and
   * nothing else — it never writes lead_activity, because ticking it is not
   * outreach and must not move a touch count. The other half, "Added on
   * LinkedIn", is an activity on the contact and lives on the lead. See
   * dealChecklist.ts and 20270122000100.
   */
  hubspot_updated: boolean;
  position: number;
  created_at: string;
}

export const OUTCOME_LABELS: Record<Deal['outcome'], string> = {
  open: 'Open',
  won: 'Won',
  lost: 'Lost',
};

export const OUTCOME_TONES: Record<Deal['outcome'], 'brand' | 'success' | 'neutral'> = {
  open: 'brand',
  won: 'success',
  lost: 'neutral',
};

/** Latest comment per deal — the Notes column is a live preview, not a field. */
export type DealNotes = Map<string, string>;

/** Deals grouped under the section they sit in, in board order. */
export function dealsBySection(deals: Deal[]): Map<string, Deal[]> {
  const grouped = new Map<string, Deal[]>();
  for (const deal of deals) {
    // A deal whose section was deleted lands under '' rather than vanishing:
    // losing sight of a live account because a section went away would be
    // worse than showing it somewhere slightly odd.
    const key = deal.section_id ?? '';
    const bucket = grouped.get(key) ?? [];
    bucket.push(deal);
    grouped.set(key, bucket);
  }
  for (const bucket of grouped.values()) {
    bucket.sort(
      (left, right) => left.position - right.position || left.name.localeCompare(right.name),
    );
  }
  return grouped;
}

/** The next position at the end of a section, leaving gaps for reordering. */
export function nextDealPosition(deals: Deal[]): number {
  return deals.reduce((highest, deal) => Math.max(highest, deal.position), 0) + 10;
}

/**
 * Whether a next-action date has passed. Compared as plain ISO dates because
 * "overdue" is a calendar judgement, not a timestamp one — a date of today is
 * due, not late, whatever the hour.
 */
export function isOverdue(nextActionDate: string | null, todayIso: string): boolean {
  return nextActionDate !== null && nextActionDate < todayIso;
}

/** One line of a comment body for the board preview: no markup, no newlines. */
export function notePreview(body: string, limit = 120): string {
  const flat = body
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return flat.length <= limit ? flat : `${flat.slice(0, limit - 1)}…`;
}
